import { Megaphone, Plus, Users } from "lucide-react";
import { useState } from "react";
import { useDataProvider, useGetList, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import { StatusBadge } from "../data-display/StatusBadge";
import { ListPageHeader } from "../list/ListPageHeader";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { CampaignDetail } from "./CampaignDetail";
import {
  type Campaign,
  CAMPAIGN_STATUS_TONES,
  type Segment,
  describeDefinition,
  emptyDefinition,
} from "./campaignModel";
import { AudiencePreview, SegmentFilterFields } from "./SegmentEditor";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

type Tab = "campaigns" | "segments";

/**
 * Campaigns & segmentation: audiences are declarative segments over CRM
 * data; every send goes through the consent-gated compose pipeline, * opted-out and do-not-contact customers are suppressed server-side.
 */
export const CampaignsPage = () => {
  const [tab, setTab] = useState<Tab>("campaigns");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: campaigns, isPending } = useGetList<Campaign>("campaigns", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });
  const { data: segments } = useGetList<Segment>("segments", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });

  if (isPending) return null;

  const selectedCampaign =
    tab === "campaigns"
      ? (campaigns ?? []).find((c) => c.id === selectedId)
      : undefined;
  const selectedSegment =
    tab === "segments"
      ? (segments ?? []).find((s) => s.id === selectedId)
      : undefined;

  return (
    <>
      <ListPageHeader
        title="Campaigns"
        subtitle="Bulk email & SMS to segmented audiences, consent-safe by design"
        actions={
          <div className="flex rounded-md border border-border p-0.5">
            {(["campaigns", "segments"] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTab(t);
                  setSelectedId(null);
                }}
                className={cn(
                  "rounded px-3 py-1 text-[12.5px] font-medium capitalize transition-colors",
                  tab === t
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t}
              </button>
            ))}
          </div>
        }
      />

      <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[300px_1fr]">
        {tab === "campaigns" ? (
          <CampaignRail
            campaigns={campaigns ?? []}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        ) : (
          <SegmentRail
            segments={segments ?? []}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        )}

        <div>
          {selectedCampaign ? (
            <CampaignDetail
              campaign={selectedCampaign}
              segments={segments ?? []}
            />
          ) : selectedSegment ? (
            <SegmentDetail segment={selectedSegment} />
          ) : (
            <SectionCard title={tab === "campaigns" ? "Campaigns" : "Segments"}>
              <div className="py-8">
                <EmptyState
                  icon={tab === "campaigns" ? Megaphone : Users}
                  title={
                    tab === "campaigns"
                      ? "Pick or create a campaign"
                      : "Pick or create a segment"
                  }
                  description={
                    tab === "campaigns"
                      ? "Campaigns send through the same consent-checked pipeline as the inbox."
                      : "Segments are saved audience filters over your CRM contacts."
                  }
                />
              </div>
            </SectionCard>
          )}
        </div>
      </div>
    </>
  );
};

const CampaignRail = ({
  campaigns,
  selectedId,
  onSelect,
}: {
  campaigns: Campaign[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [creating, setCreating] = useState(false);
  const [folderFilter, setFolderFilter] = useState("");

  const folders = Array.from(
    new Set(
      campaigns
        .map((c) => (c.folder ?? "").trim())
        .filter((folder) => folder !== ""),
    ),
  ).sort();
  const visibleCampaigns =
    folderFilter === ""
      ? campaigns
      : folderFilter === "__unfiled__"
        ? campaigns.filter((c) => !(c.folder ?? "").trim())
        : campaigns.filter((c) => (c.folder ?? "").trim() === folderFilter);

  const createCampaign = async () => {
    setCreating(true);
    try {
      const { data } = await dataProvider.create("campaigns", {
        data: {
          name: "Untitled campaign",
          channel: "email",
          body: "",
          workspace_id: getActiveWorkspaceId(),
        },
      });
      refresh();
      onSelect(String((data as { id: string }).id));
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not create campaign",
        { type: "error" },
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <SectionCard title="Your campaigns" contentClassName="px-2 pb-2">
      <Button
        size="sm"
        className="mx-3 mb-2 w-[calc(100%-1.5rem)]"
        disabled={creating}
        onClick={createCampaign}
      >
        <Plus className="mr-1 size-3.5" />
        New campaign
      </Button>
      {folders.length > 0 ? (
        <select
          className="mx-3 mb-2 h-8 w-[calc(100%-1.5rem)] rounded-md border border-input bg-transparent px-2 text-[12.5px] outline-none"
          value={folderFilter}
          onChange={(e) => setFolderFilter(e.target.value)}
        >
          <option value="">All folders</option>
          {folders.map((folder) => (
            <option key={folder} value={folder}>
              {folder}
            </option>
          ))}
          <option value="__unfiled__">Unfiled</option>
        </select>
      ) : null}
      <datalist id="gravun-campaign-folders">
        {folders.map((folder) => (
          <option key={folder} value={folder} />
        ))}
      </datalist>
      <div className="flex max-h-[520px] flex-col overflow-y-auto">
        {visibleCampaigns.map((campaign) => (
          <button
            key={campaign.id}
            type="button"
            onClick={() => onSelect(campaign.id)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-[13px] transition-colors",
              selectedId === campaign.id
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent/50",
            )}
          >
            <span className="min-w-0">
              <span className="block truncate font-medium">
                {campaign.name}
              </span>
              <span className="block text-[11.5px]">
                {campaign.channel.toUpperCase()} ·{" "}
                {new Date(campaign.created_at).toLocaleDateString()}
              </span>
            </span>
            <StatusBadge
              label={campaign.status}
              tone={CAMPAIGN_STATUS_TONES[campaign.status] ?? "neutral"}
            />
          </button>
        ))}
      </div>
    </SectionCard>
  );
};

const SegmentRail = ({
  segments,
  selectedId,
  onSelect,
}: {
  segments: Segment[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [creating, setCreating] = useState(false);

  const createSegment = async () => {
    setCreating(true);
    try {
      const { data } = await dataProvider.create("segments", {
        data: {
          name: "New segment",
          definition: emptyDefinition(),
          workspace_id: getActiveWorkspaceId(),
        },
      });
      refresh();
      onSelect(String((data as { id: string }).id));
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not create segment",
        { type: "error" },
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <SectionCard title="Your segments" contentClassName="px-2 pb-2">
      <Button
        size="sm"
        className="mx-3 mb-2 w-[calc(100%-1.5rem)]"
        disabled={creating}
        onClick={createSegment}
      >
        <Plus className="mr-1 size-3.5" />
        New segment
      </Button>
      <div className="flex max-h-[520px] flex-col overflow-y-auto">
        {segments.map((segment) => (
          <button
            key={segment.id}
            type="button"
            onClick={() => onSelect(segment.id)}
            className={cn(
              "flex flex-col rounded-md px-3 py-2 text-left text-[13px] transition-colors",
              selectedId === segment.id
                ? "bg-accent text-foreground"
                : "text-muted-foreground hover:bg-accent/50",
            )}
          >
            <span className="truncate font-medium">{segment.name}</span>
            <span className="text-[11.5px]">
              {describeDefinition(segment.definition)}
            </span>
          </button>
        ))}
      </div>
    </SectionCard>
  );
};

const SegmentDetail = ({ segment }: { segment: Segment }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [name, setName] = useState(segment.name);
  const [description, setDescription] = useState(segment.description ?? "");
  const [definition, setDefinition] = useState(
    segment.definition ?? emptyDefinition(),
  );
  const [saving, setSaving] = useState(false);
  const [loadedId, setLoadedId] = useState(segment.id);

  if (loadedId !== segment.id) {
    setLoadedId(segment.id);
    setName(segment.name);
    setDescription(segment.description ?? "");
    setDefinition(segment.definition ?? emptyDefinition());
  }

  const save = async () => {
    setSaving(true);
    try {
      await dataProvider.update("segments", {
        id: segment.id,
        data: {
          name: name.trim() || "Untitled segment",
          description: description.trim() || null,
          definition,
        },
        previousData: segment,
      });
      notify("Segment saved");
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not save", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={Users}
        title={segment.name}
        subtitle="Filters combine with AND; leave everything empty to match all contacts"
      >
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Name
              <input
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Description
              <input
                className={inputClass}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </div>
          <SegmentFilterFields
            definition={definition}
            onChange={setDefinition}
          />
          <div className="flex justify-end border-t border-border-subtle pt-3">
            <Button size="sm" disabled={saving} onClick={save}>
              Save segment
            </Button>
          </div>
        </div>
      </SectionCard>

      <AudiencePreview definition={definition} channel="email" />
    </div>
  );
};
